import { AreaMeasurementUnitApi } from '@myre/app-am-generated/sdk/angular/AreaMeasurementUnitApi';
import { CurrencyApi } from '@myre/app-am-generated/sdk/angular/CurrencyApi';
import { IndustryApi } from '@myre/app-am-generated/sdk/angular/IndustryApi';
import { myreAppSlugMap } from '@myre/ui-domain-core';

import { inject, Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { AuthService } from '../../guards/auth.service';
import { BaseEffects } from '../../shared/base.effects';
import { CompanyTypeService } from '../../shared/services/company-type.service';
import { CountryService } from '../../shared/services/country.service';

import {
  AcceptInvitationError,
  AcceptInvitationSuccess,
  CORE_ACTION_ACCEPT_INVITATION_REQUEST,
  CORE_ACTION_GET_COMPANY_TYPES_REQUEST,
  CORE_ACTION_GET_COUNTRIES_REQUEST,
  CORE_ACTION_GET_INDUSTRIES_REQUEST,
  getAreaMeasurementUnitsError,
  getAreaMeasurementUnitsRequest,
  getAreaMeasurementUnitsSuccess,
  GetCompanyTypesError,
  GetCompanyTypesSuccess,
  GetCountriesError,
  GetCountriesSuccess,
  getCurrenciesError,
  getCurrenciesRequest,
  getCurrenciesSuccess,
  GetIndustriesError,
  GetIndustriesSuccess,
  type AcceptInvitationRequest,
  type GetCompanyTypesRequest,
  type GetCountriesRequest,
  type GetIndustriesRequest,
} from './core.actions';

@Injectable({ providedIn: 'root' })
export class CoreEffects extends BaseEffects {
  private readonly _authService = inject(AuthService);
  private readonly _companyTypeService = inject(CompanyTypeService);
  private readonly _countryService = inject(CountryService);
  private readonly _currencyApi = inject(CurrencyApi);
  private readonly _areaMeasurementUnitApi = inject(AreaMeasurementUnitApi);
  private readonly _industryApi = inject(IndustryApi);

  public getIndustries$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetIndustriesRequest>(CORE_ACTION_GET_INDUSTRIES_REQUEST),
      switchMap(() => {
        return this._industryApi.getIndustryList().pipe(
          map(industries => new GetIndustriesSuccess(industries)),
          catchError(error => of(new GetIndustriesError(error))),
        );
      }),
    ),
  );

  public getCountries$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetCountriesRequest>(CORE_ACTION_GET_COUNTRIES_REQUEST),
      switchMap(() => {
        return this._countryService.getCountries().pipe(
          map(countries => new GetCountriesSuccess(countries)),
          catchError(error => of(new GetCountriesError(error))),
        );
      }),
    ),
  );

  public getCompanyTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetCompanyTypesRequest>(CORE_ACTION_GET_COMPANY_TYPES_REQUEST),
      switchMap(() => {
        return this._companyTypeService.getCompanyTypes().pipe(
          map(companyTypes => new GetCompanyTypesSuccess(companyTypes)),
          catchError(error => of(new GetCompanyTypesError(error))),
        );
      }),
    ),
  );

  public getCurrencies$ = createEffect(() =>
    this._actions$.pipe(
      ofType(getCurrenciesRequest),
      switchMap(() => {
        return this._currencyApi.getAllCurrencies().pipe(
          map(currencies => getCurrenciesSuccess({ currencies })),
          catchError(error => of(getCurrenciesError(error))),
        );
      }),
    ),
  );

  public getAreaMeasurementUnits$ = createEffect(() =>
    this._actions$.pipe(
      ofType(getAreaMeasurementUnitsRequest),
      switchMap(() => {
        return this._areaMeasurementUnitApi.getAllAreaMeasurementUnits().pipe(
          map(areaMeasurementUnits =>
            getAreaMeasurementUnitsSuccess({
              areaMeasurementUnits,
            }),
          ),
          catchError(error => of(getAreaMeasurementUnitsError(error))),
        );
      }),
    ),
  );

  public acceptInvitation$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AcceptInvitationRequest>(CORE_ACTION_ACCEPT_INVITATION_REQUEST),
      mergeMap(action => {
        return this._authService
          .acceptInvitation(action.product.invitationId)
          .pipe(
            mergeMap(() => this._authService.auth()),
            map(() => {
              this._authService.navigateToApp(
                {
                  customerId: action.product.customerId,
                },
                myreAppSlugMap[action.product.appId],
              );

              return new AcceptInvitationSuccess(action.product);
            }),
            catchError(error => of(new AcceptInvitationError(error))),
          );
      }),
    ),
  );
}
